import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql';
import { getIsActivityV3RecentsEnabled } from '@confluence/activity-v3-enabled/entry-points/getIsActivityV3RecentsEnabled';
import { preloadIntentBasedOnboarding } from '@confluence/home/entry-points/preloadIntentBasedOnboarding';
import { preloadModernizeConfluenceOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadModernizeConfluenceOnboardingState';
import { preloadAutoFollowSpacesState } from '@confluence/onboarding-helpers/entry-points/preloadAutoFollowSpacesState';
import { preloadAutoFollowTeammatesState } from '@confluence/onboarding-helpers/entry-points/preloadAutoFollowTeammatesState';
import { preloadTemplatesCollectionEligible } from '@confluence/home/entry-points/preloadTemplatesCollectionEligible';
import { preloadFeed } from '@confluence/feed/entry-points/preloadFeed';
import { preloadPostOfficeConfluenceHomeBanner } from '@confluence/experiment-post-office-home-banner/entry-points/preloadConfuenceHomeBanner';
import { preloadPersonalizedTemplates } from '@confluence/experiment-personalized-templates-v2/entry-points/preloadPersonalizedTemplates';
import { preloadNthUserGettingStartedOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadNthUserGettingStartedOnboardingState';
import { preloadUseCasesNthUserOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadUseCasesNthUserOnboardingState';
import { SSRMeasures } from '@confluence/action-measures/entry-points/SSRMeasures';
import { preloadPersistentInviteButtonEligible } from '@confluence/persistent-invite-button/entry-points/preloadPersistentInviteEligible';
import { preloadRovoAwarenessOnboardingState } from '@confluence/onboarding-helpers/entry-points/preloadRovoAwareness';
import { preloadHomeOnboardingTakeoverV2 } from '@confluence/onboarding-helpers/entry-points/preloadHomeOnboardingTakeoverV2';
import { expVal, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext';

import { preloadSidePanel } from '../../preloadSidePanel';

import { getRecentWorkQueryOptions } from './recentWorkQueryHelpers';
import { ALL_RECENT_ITEMS_COUNT_HOME_REFRESH } from './recentListConstants';

const preloadRecentWork = (args: {
	cloudId: string;
	isLoggedIn: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
	first?: number;
	isActivityV3MigrationEnabled: boolean;
}) => {
	const queryOptions = getRecentWorkQueryOptions(args);

	if (queryOptions.skip) {
		return Promise.resolve({});
	}

	/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	return query(queryOptions);
};

export const preloadForYou = ({
	userId,
	cloudId,
	isLicensed,
	isWhiteboardViewedEnabled = false,
	isDatabaseEnabled = false,
}: {
	userId: string | null;
	cloudId: string;
	isLicensed: boolean;
	isWhiteboardViewedEnabled?: boolean;
	isDatabaseEnabled?: boolean;
}) => {
	const preloadedSessionContext = getPreloaderFnContext();
	const { accessStatus } = preloadedSessionContext;
	const canAccessHome =
		accessStatus !== 'ANONYMOUS_ACCESS' && accessStatus !== 'UNLICENSED_AUTHENTICATED_ACCESS';
	const isEnglishLocale = preloadedSessionContext.locale?.toLowerCase()?.startsWith('en');

	const tasks = [
		preloadRecentWork({
			cloudId,
			isLoggedIn: !!userId,
			isWhiteboardViewedEnabled,
			isDatabaseEnabled,
			first: ALL_RECENT_ITEMS_COUNT_HOME_REFRESH,
			isActivityV3MigrationEnabled: getIsActivityV3RecentsEnabled(),
		}),
		preloadFeed({
			isLoggedIn: !!userId,
		}),
		preloadSidePanel({
			isLicensed,
		}),
	];

	if (userId) {
		tasks.push(
			preloadIntentBasedOnboarding(),
			preloadTemplatesCollectionEligible(),
			preloadModernizeConfluenceOnboardingState(),
		);

		if (isLicensed && canAccessHome) {
			if (fg('cc-engagement-auto-follow-recommended-spaces-gate')) {
				tasks.push(preloadAutoFollowSpacesState());
			}

			if (fg('cc-engagement-getting-started-nth-user-gate')) {
				tasks.push(preloadNthUserGettingStartedOnboardingState());
			}

			if (fg('autofollow-teammates-preconditions')) {
				tasks.push(preloadAutoFollowTeammatesState());
			}

			if (isEnglishLocale && fg('rovo_awareness_enabled')) {
				tasks.push(preloadRovoAwarenessOnboardingState());
			}

			tasks.push(preloadHomeOnboardingTakeoverV2());
		}
	}

	if (isLicensed && canAccessHome && userId && fg('cc-ssr-preload-use-cases-nth-user-onboarding')) {
		tasks.push(preloadUseCasesNthUserOnboardingState());
	}

	if (
		isLicensed &&
		canAccessHome &&
		userId &&
		accessStatus &&
		fg('cc-ssr-preload-personalized-templates')
	) {
		tasks.push(preloadPersonalizedTemplates(userId, accessStatus));
	}

	const unifiedSpacePreloader = preloadPersistentInviteButtonEligible(
		{ spaceKey: undefined },
		expVal('confluence_unified_space_preloader', 'isEnabled', false),
	);

	if (unifiedSpacePreloader) {
		// @ts-ignore-next-line
		tasks.push(unifiedSpacePreloader); //type can be undefined, but the surrounding conditional prevents it
	}

	const isLocaleEligible = fg('post-office_confluence-home-banner-v2-locale-check')
		? preloadedSessionContext.locale.toLowerCase().startsWith('en')
		: true;
	if (
		userId &&
		// Manually check locale in code, instead of using Statsig, because CCFE does not have locale populated with value during FG initialization yet.
		isLocaleEligible &&
		// suppress exposure because if feed is empty, users do not see the banner even if they are initially assigned to be in Variation
		UNSAFE_expValEqualsNoExposure(
			'post_office_confluence_home_banner_experiment_-_v2',
			'cohort',
			'variation',
		)
	) {
		// Confluence Home Banner in SSR
		const preloadPostOfficeHomeBanner = async () => {
			return SSRMeasures.run(
				'ssr-app/prepare/preloadQuery/postOffice:ConfluenceHomeBanner',
				async () => {
					const result = await preloadPostOfficeConfluenceHomeBanner(preloadedSessionContext);
					return result ?? {};
				},
			);
		};

		tasks.push(preloadPostOfficeHomeBanner());
	}

	return waitForAllReturnWhenError(tasks);
};
